import React, { useEffect, useState } from "react"
import { Box, Button, Card, Grid, IconButton, Link, MenuItem, Select, Typography } from "@material-ui/core"

import {
	Add as AddIcon,
	CloseOutlined as LostIcon,
	CompareArrowsOutlined as DefaultStageIcon,
	DeleteOutlined as DeleteIcon,
	Edit as EditIcon,
	EmojiEventsOutlined as WonIcon
} from "@material-ui/icons"
import { Text } from "letalkui"

import { BoardOutlinedIcon } from "@/assets/icons"

import { ActionDialog, ContactValueDialog, Loading, Notification } from "@/components"

import { ErrorType } from "@/hooks/useValidation"
import useSocket from "@/hooks/useSocket"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"

import ClientBoardSkeleton from "@/skeletons/ClientBoard"

import BoardStageService, { BoardStage } from "@/services/BoardStage"
import BoardStageCardService, { BoardCardStatus } from "@/services/BoardStageCardService"
import BoardService, { Board } from "@/services/BoardService"
import ErrorHandlerService from "@/services/ErrorHandler"

import { useChatGlobalStateStore } from "@/store/ChatGlobalState"

import { convertCentsInReals } from "@/utils/currencyFormatter"
import { InboxV2Links } from "@/utils/link"

import StatusConfirmationModal from "@/pages/Attendance/Chat/ConversationPanel/ClientInfo/ClientBoard/StatusConfirmationModal"

import useCustomStyles from "@/styles/custom"
import useStyles from "@/pages/Attendance/Chat/ConversationPanel/ClientInfo/ClientBoard/styles"
import { colors } from "@/styles/colors"

type ClientBoardCardData = {
	card: {
		id: number
		boardId: number
		stageId: number
		clientId: number
	}
}

type InstanceBoardData = {
	board?: Board & {
		lossReasons?: Array<{ id: number, reasonText: string }>
		lossOtherEnabled?: boolean
		settings?: { isContactValueRequired: boolean, isResponsibleUserRequired: boolean }
	}
}

type ClientBoardProps = {
	clientId: number
}

const ClientBoard: React.FC<ClientBoardProps> = (props) => {
	const {
		clientId
	} = props

	const socket = useSocket()

	const chatGlobalStateStore = useChatGlobalStateStore()

	const customClasses = useCustomStyles()
	const classes = useStyles()

	const [clientBoardData, setClientBoardData] = useState<ClientBoardCardData | null>(null)
	const [instanceBoardData, setInstanceBoardData] = useState<InstanceBoardData | null>(null)

	const [isLoading, setLoading] = useState<boolean>(false)
	const [openRemoveContactFromBoardDialog, setOpenRemoveContactFromBoardDialog] = useState(false)
	const [openContactValueDialog, setOpenContactValueDialog] = useState(false)
	const [statusConfirmationModalData, setStatusConfirmationModalData] = useState<{
		targetStageId: number
		status: BoardCardStatus
	} | null>(null)

	const funnelSubscriptionData = useSubscriptionLimits("permission", "funnel")
	const canUseContactValue = !funnelSubscriptionData.mustBlock

	const client = chatGlobalStateStore.client.getById(clientId)
	const contactValueInCents = client?.cardInfoExtraData?.contactValueInCents
	const hasContactValueDefined = contactValueInCents !== undefined && contactValueInCents !== null

	const boardDetailsPath = `${InboxV2Links.boardDetails}/${clientBoardData?.card?.boardId}`

	const handleOpenContactValueDialog = (): void => {
		setOpenContactValueDialog(true)
	}

	const handleCloseContactValueDialog = (): void => {
		setOpenContactValueDialog(false)
	}

	const handleContactValueSaved = (newContactValueInCents: number): void => {
		chatGlobalStateStore.client.updateById(clientId, {
			cardInfoExtraData: {
				contactValueInCents: newContactValueInCents
			}
		})
	}

	const handleOpenRemoveContactFromBoardDialog = async () => {
		setOpenRemoveContactFromBoardDialog(true)
	}

	const handleCloseRemoveContactFromBoardDialog = () => {
		setOpenRemoveContactFromBoardDialog(false)
	}

	const loadClientBoardData = async (): Promise<void> => {
		try {
			const card = await BoardStageCardService.getCardByClientId(clientId)

			setClientBoardData({
				card: {
					id: card.id,
					boardId: card.boardId,
					stageId: card.stageId,
					clientId
				}
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const loadInstanceBoardData = async (): Promise<void> => {
		try {
			const boards = await BoardService.retrieveAllBoardsWithStagesNoCards()
			/*
				Although the API returns an array of boards, initially each instance will only be allowed to have a single board.
				Therefore, for now, we're using the first board in the array (index 0) directly, without providing a selection option.
				In the future, when support for multiple boards per instance is implemented, this logic can be updated accordingly.
			*/
			const instanceBoard = boards[0]

			const stages = await BoardStageService.getBoardStagesByBoardId(instanceBoard.id)

			setInstanceBoardData({
				board: {
					...instanceBoard,
					stages
				}
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const handleChangeClientBoardData = (targetStageId: number) => {
		setClientBoardData((prev) => {
			if (!prev) {
				return prev
			}

			return {
				card: {
					...prev.card,
					stageId: targetStageId
				}
			}
		})
	}

	const handleOnChangeStage = async (targetStageId: number): Promise<void> => {
		const targetStage = instanceBoardData?.board?.stages.find(stage => stage.id === targetStageId)
		const targetStageProperty = targetStage?.property

		if (targetStageProperty) {
			setStatusConfirmationModalData({
				targetStageId,
				status: targetStageProperty.value
			})
			return
		}

		try {
			const cardId = clientBoardData?.card.id

			if (!cardId) {
				throw Error()
			}

			await BoardStageCardService.moveCardBetweenStages({
				cardId,
				targetStageId
			})

			handleChangeClientBoardData(targetStageId)

			Notification.success({
				message: `Contato adicionado ao funil na etapa "${targetStage?.name}"`
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)

			Notification.error({
				message: "Houve um erro ao mover contato para a etapa."
			})
		}
	}

	const handleCloseStatusConfirmationModal = (): void => {
		setStatusConfirmationModalData(null)
	}

	const handleStatusConfirmationUpdate = (): void => {
		if (statusConfirmationModalData) {
			handleChangeClientBoardData(statusConfirmationModalData.targetStageId)
		}
		setStatusConfirmationModalData(null)
	}

	const handleOnDeleteFromBoard = async (): Promise<void> => {
		const boardId = instanceBoardData?.board?.id as number
		const stageId = clientBoardData?.card?.stageId as number
		const cardId = clientBoardData?.card?.id as number

		try {
			await BoardStageCardService.removeCardFromBoardStage({
				board: {
					id: boardId
				},
				stage: {
					id: stageId
				},
				card: {
					id: cardId
				}
			})

			setClientBoardData(null)
			chatGlobalStateStore.client.updateById(clientId, { cardInfoExtraData: { contactValueInCents: null } })

			handleCloseRemoveContactFromBoardDialog()

			Notification.success({
				message: "Contato removido do board com sucesso."
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)

			Notification.error({
				message: "Houve um erro ao deletar contato do board."
			})
		}
	}

	const handleOnAddClientToBoard = async (): Promise<void> => {
		/*
			Contacts added via inbox must start in the board's first stage (lowest order)
		*/
		const boardFirstStage = instanceBoardData?.board?.stages.reduce((minStage, currentStage) =>
			currentStage.stageOrder < minStage.stageOrder ? currentStage : minStage
		)

		const boardId = instanceBoardData?.board?.id as number

		try {
			const card = await BoardStageCardService.createBoardStageCard({
				board: {
					id: boardId
				},
				stage: {
					id: boardFirstStage?.id as number
				},
				client: {
					id: clientId
				},
				createdFrom: "INBOX"
			})

			setClientBoardData({
				card: {
					id: card.id,
					boardId: card.boardId,
					stageId: card.stageId,
					clientId
				}
			})

			Notification.success({
				message: `Contato adicionado ao funil na etapa "${boardFirstStage?.name}"`
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			Notification.error({
				message: `Houve um erro ao adicionar o contato na etapa "${boardFirstStage?.name}"`
			})
		}
	}

	const handleOnChangeBoardStages = async (boardStage: BoardStage): Promise<void> => {
		const stages = await BoardStageService.getBoardStagesByBoardId(boardStage.boardId)
		if (!stages) {
			return
		}

		setInstanceBoardData((prev) => {
			if (!prev?.board) {
				return prev
			}

			return {
				board: {
					...prev.board,
					stages
				}
			}
		})
	}

	const setupSocket = async () => {
		socket.onBoardStageCardCreated(async boardStageCard => {
			const wasCurrentClientAddedToBoard = boardStageCard.clients.some((client) => client.clientId === clientId)

			if (wasCurrentClientAddedToBoard) {
				setClientBoardData({
					card: {
						id: boardStageCard.id,
						boardId: boardStageCard.boardId,
						stageId: boardStageCard.stageId,
						clientId
					}
				})
			}
		})

		socket.onBoardStageCardDeleted(async boardStageCardDeleted => {
			const wasCurrentClientRemovedFromBoard = boardStageCardDeleted.clientIds.some(removedCardClientId => removedCardClientId === clientId)
			if (wasCurrentClientRemovedFromBoard) {
				setClientBoardData(null)
				chatGlobalStateStore.client.updateById(clientId, { cardInfoExtraData: { contactValueInCents: null } })
			}
		})

		socket.onBoardStageCardMoved(async boardStageCardMoved => {
			const wasCurrentClientMoved = boardStageCardMoved.clientIds.some(movedCardClientId => movedCardClientId === clientId)
			if (wasCurrentClientMoved) {
				handleChangeClientBoardData(boardStageCardMoved.targetStageId)
			}
		})

		socket.onBoardStageCreated(async boardStage => {
			await handleOnChangeBoardStages(boardStage)
		})

		socket.onBoardStageDeleted(async boardStage => {
			await handleOnChangeBoardStages(boardStage)
		})

		socket.onBoardStageRenamed(async boardStage => {
			await handleOnChangeBoardStages(boardStage)
		})

		socket.onBoardStageMoved(async boardStage => {
			await handleOnChangeBoardStages(boardStage)
		})
	}

	const loadInitialData = async (): Promise<void> => {
		setLoading(true)
		await loadClientBoardData()
		await loadInstanceBoardData()
		setLoading(false)
	}

	const removeContactFromBoardDialog = () => {
		return (
			<ActionDialog
				openDialog={openRemoveContactFromBoardDialog}
				loading={isLoading}
				title="Remover contato do funil"
				saveText="Confirmar"
				cancelText="Cancelar"
				onClose={handleCloseRemoveContactFromBoardDialog}
				onSave={handleOnDeleteFromBoard}
				maxWidth="sm"
				fullWidth
			>
				<Typography
					variant="body1"
				>
					Confirma a remoção do contato do funil? O valor do contato também será removido. Essa ação é irreversível.
				</Typography>
			</ActionDialog>
		)
	}

	const AddClientToBoardButton = () => {
		return (
			<Grid item xs>
				<Button
					startIcon={<AddIcon />}
					variant="contained"
					className={customClasses.secondaryActionButton}
					fullWidth
					onClick={handleOnAddClientToBoard}
				>
					Adicionar ao funil
				</Button>
			</Grid>
		)
	}

	const ClientBoardStageSelector = () => {
		return (
			<Grid item xs>
				<Grid container direction="column" justifyContent="space-between" spacing={1}>
					<Grid item>
						<Grid
							container
							justifyContent="space-between"
							alignItems="center"
						>
							<Grid item>
								<Text.Body
									type="small1"
									fontWeight="regular"
									color={colors.gray[700]}
								>
									Etapa do funil
								</Text.Body>
							</Grid>
							<Grid item>
								<Grid container>
									<Link
										href={boardDetailsPath}
										target="_blank"
										rel="noreferrer"
									>
										<IconButton style={{ padding: 0 }}>
											<BoardOutlinedIcon />
										</IconButton>
									</Link>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<Select
							value={clientBoardData?.card?.stageId}
							variant="outlined"
							fullWidth
							onChange={(event) => {
								handleOnChangeStage(event.target.value as number)
							}}
							className={classes.selectInput}
							renderValue={(stageId) => {
								const currentStage = instanceBoardData?.board?.stages.find(stage => stage.id === stageId)
								const stageName = currentStage?.name
								const propertyValue = currentStage?.property?.value

								const propertyValueToIcon: Record<BoardCardStatus, React.ReactElement> = {
									won: <WonIcon className={classes.wonStageIcon} />,
									lost: <LostIcon className={classes.lostStageIcon} />
								}

								const stageIcon = propertyValue
									? propertyValueToIcon[propertyValue]
									: <DefaultStageIcon className={classes.defaultStageIcon} />

								return (
									<Box display="flex" alignItems="center">
										{stageIcon}
										<Typography
											variant="h4"
											color="textPrimary"
											style={{
												fontSize: "14px",
												fontWeight: "500",
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
												maxWidth: "250px"
											}}
										>
											{stageName}
										</Typography>
									</Box>
								)
							}}
						>
							{instanceBoardData?.board?.stages.map((stage) => (
								<MenuItem key={stage.id} value={stage.id}>
									{stage.name}
								</MenuItem>
							))}
						</Select>
					</Grid>
				</Grid>
			</Grid>
		)
	}

	const RemoveContactFromBoardLink = () => {
		return (
			<Grid item xs>
				<Grid container justifyContent="center">
					<Grid item>
						<Link
							onClick={handleOpenRemoveContactFromBoardDialog}
							className={classes.removeContactFromBoardText}
						>
							<DeleteIcon className={classes.removeContactFromBoardIcon} />
							<Typography
								style={{ fontSize: "14px", fontWeight: "400" }}
							>
								Remover contato do funil
							</Typography>
						</Link>
					</Grid>
				</Grid>
			</Grid>
		)
	}

	const ContactValueDefinedContainer = () => {
		return (
			<Grid
				container
				alignItems="center"
				justifyContent="space-between"
				className={`${classes.contactValueContainer} ${classes.contactValueClickable}`}
				onClick={handleOpenContactValueDialog}
			>
				<Grid item>
					<Typography className={classes.contactValueText}>
						{convertCentsInReals(contactValueInCents as number)}
					</Typography>
				</Grid>

				<Grid item>
					<EditIcon className={classes.contactValueEditIcon} />
				</Grid>
			</Grid>
		)
	}

	const ContactValueEmptyContainer = () => {
		return (
			<Grid
				container
				alignItems="center"
				className={`${classes.contactValueEmptyChip} ${classes.contactValueClickable}`}
				onClick={handleOpenContactValueDialog}
			>
				<AddIcon className={classes.contactValueEmptyChipIcon} />
				<Typography className={classes.contactValueEmptyChipText}>
					Definir valor
				</Typography>
			</Grid>
		)
	}

	const ContactValueSection = () => {
		return (
			<Grid item xs>
				<Grid container direction="column" spacing={1}>
					<Grid item>
						<Text.Body
							type="small1"
							fontWeight="regular"
							color={colors.gray[700]}
						>
							Valor do contato
						</Text.Body>
					</Grid>
					<Grid item>
						{hasContactValueDefined ? <ContactValueDefinedContainer /> : <ContactValueEmptyContainer />}
					</Grid>
				</Grid>
			</Grid>
		)
	}

	useEffect(() => {
		loadInitialData()
		setupSocket()
	}, [clientId])

	return (
		<>
			<Grid item>
				<Loading loading={isLoading} customLoadingElement={<ClientBoardSkeleton />}>
					<Card className={classes.boardCard}>
						<Grid container direction="column" alignItems="stretch" className={classes.boardContent}>
							<Grid container alignItems="center">
								{!clientBoardData?.card ? <AddClientToBoardButton /> : <ClientBoardStageSelector />}
							</Grid>

							{canUseContactValue && clientBoardData?.card && (
								<Grid container alignItems="center" className={classes.contactValueSectionContainer}>
									<ContactValueSection />
								</Grid>
							)}

							{clientBoardData?.card && (
								<Grid container alignItems="center" className={classes.removeContactFromBoardContainer}>
									<RemoveContactFromBoardLink />
								</Grid>
							)}
						</Grid>
					</Card>
				</Loading>
			</Grid>

			{removeContactFromBoardDialog()}

			<ContactValueDialog
				key={String(openContactValueDialog)}
				open={openContactValueDialog}
				clientId={clientId}
				currentValueInCents={contactValueInCents}
				isContactValueRequired={instanceBoardData?.board?.settings?.isContactValueRequired}
				onClose={handleCloseContactValueDialog}
				onSaved={handleContactValueSaved}
			/>

			{statusConfirmationModalData && (
				<StatusConfirmationModal
					open={Boolean(statusConfirmationModalData)}
					boardId={instanceBoardData?.board?.id as number}
					cardId={clientBoardData?.card.id as number}
					cardName={client?.nickname ?? ""}
					targetStageId={statusConfirmationModalData.targetStageId}
					status={statusConfirmationModalData.status}
					lossReasons={instanceBoardData?.board?.lossReasons ?? []}
					lossOtherEnabled={instanceBoardData?.board?.lossOtherEnabled ?? false}
					settings={instanceBoardData?.board?.settings}
					responsibleUserId={client?.account_manager_user_id}
					contactValueInCents={contactValueInCents}
					onClose={handleCloseStatusConfirmationModal}
					onUpdate={handleStatusConfirmationUpdate}
				/>
			)}
		</>
	)
}

export default ClientBoard
